import { includes } from "lodash-es";
import { resourceTypes } from "../../Findings/FindingsUtils";
import { SYSTEM } from "../../Findings/FindingsFilters/FilterUtils";
import type { TreeNode } from "./types";

export const TristateStateEnum = {
  CHECKED: "checked",
  INDETERMINATE: "indeterminate",
} as const;

export type TristateState =
  (typeof TristateStateEnum)[keyof typeof TristateStateEnum];

export const getNodeById = <T extends TreeNode>(
  node: T | null | undefined,
  id: string
): T | null => {
  if (node?.id?.toString() === id) {
    return node;
  } else if (node?.children?.length) {
    let result: T | null = null;
    for (let i = 0; result === null && i < node?.children?.length; i++) {
      result = getNodeById(node.children[i] as T, id);
    }
    return result;
  }
  return null;
};

export const updateChildrenWithParentId = (
  nodes: TreeNode[],
  parentId: string | null
): TreeNode[] => {
  return nodes.reduce<TreeNode[]>((acc, node) => {
    if (node?.children?.length) {
      let newItems = updateChildrenWithParentId(node.children, node.id ?? null);
      if (newItems?.length) {
        acc.push({
          ...node,
          children: newItems,
          parentId,
        });
      }
    } else {
      acc.push({ ...node, parentId });
    }
    return acc;
  }, []);
};

// Length is measured on the raw value, not trimmed: spaces are legitimate
// characters inside scope names ("Prod EU").
export const MIN_SEARCH_LENGTH = 3;
export const MIN_SEARCH_LENGTH_HINT = `(type at least ${MIN_SEARCH_LENGTH} characters)`;
// Only trees above this node count are worth gating - below it, filtering on a
// single character is fast enough.
export const MIN_SEARCH_LENGTH_NODE_THRESHOLD = 2000;
export const SEARCH_DEBOUNCE_MS = 200;
// A single group can hold thousands of children - they are rendered a page at
// a time so expanding it does not mount every row at once.
export const CHILDREN_PAGE_SIZE = 200;

export const isSearchTermActive = (
  value: string | undefined,
  minLength: number
): boolean => (value?.length ?? 0) >= minLength;

// TreeNode is listed explicitly: its index signature alone fails TS's
// weak-type check against ManagedNodeFields.
interface ManagedNodeFields {
  editable?: unknown;
  created_by?: unknown;
}
export const isUserEditableNode = (
  node: TreeNode | ManagedNodeFields | null | undefined
): boolean => {
  const fields = node as ManagedNodeFields | null | undefined;
  return !!fields?.editable && fields?.created_by !== SYSTEM;
};

export const countNodes = (nodes: TreeNode[] | null | undefined): number =>
  (nodes ?? []).reduce(
    (total, node) => total + 1 + countNodes(node?.children),
    0
  );

export const search = (
  treeData: TreeNode[],
  searchValue: string
): TreeNode[] => {
  if (!searchValue) {
    return treeData;
  }
  return treeData.reduce<TreeNode[]>((acc, node) => {
    if (includes(node?.name?.toLowerCase(), searchValue?.toLowerCase())) {
      let newItems = node?.children
        ? search(node?.children, searchValue)
        : node?.children;
      acc.push({ ...node, children: newItems });
    } else if ((node?.children?.length ?? 0) > 0) {
      let newItems = search(node.children ?? [], searchValue);
      if (newItems?.length > 0) {
        acc.push({
          ...node,
          children: newItems,
        });
      }
    }

    return acc;
  }, []);
};

export const getGroupsTree = (treeData: TreeNode[]): TreeNode[] => {
  return treeData.reduce<TreeNode[]>((acc, node) => {
    if (
      node?.type === resourceTypes.ROOT ||
      node?.type === resourceTypes?.GROUP
    ) {
      let newItems = node?.children
        ? getGroupsTree(node?.children)
        : node?.children;
      acc.push({ ...node, children: newItems });
    } else if ((node?.children?.length ?? 0) > 0) {
      let newItems = getGroupsTree(node.children ?? []);
      if (newItems?.length > 0) {
        acc.push({
          ...node,
          children: newItems,
        });
      }
    }

    return acc;
  }, []);
};

export const getIconByType = (type: string): string => {
  switch (type) {
    case resourceTypes.GROUP:
      return "seem-icon-scope-group";

    case resourceTypes.SCOPE:
      return "seem-icon-scope";

    case resourceTypes.ROOT:
      return "seem-icon-root";

    case resourceTypes.UNSCOPED:
      return "seem-icon-unscoped";

    default:
      return "";
  }
};

export const flatTree = (node: TreeNode): TreeNode[] => {
  let nodes: TreeNode[] = [];
  const traverse = (node: TreeNode) => {
    nodes.push(node);
    node?.children?.forEach((child) => {
      traverse(child);
    });
  };

  traverse(node);
  return nodes;
};
